import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { useTranslation } from "react-i18next";

const BatteryIndicator = styled(CircularProgress)(({ theme, battery }) => ({
  color:
    battery <= 30
      ? theme.palette.error.main
      : battery <= 50
      ? theme.palette.warning.main
      : theme.palette.success.main,
  width: "60px !important",
  height: "60px !important",
}));

function DeviceCard({
  battery_level,
  android_id,
  payload_timestamp,
  model,
  brand,
  latitude,
  longitude,
  pos_pid,
}) {
  const { t } = useTranslation("common");
  const calculateTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return t("timeAgo.days", { count: days });
    if (hours > 0) return t("timeAgo.hours", { count: hours });
    return t("timeAgo.lessThanHour");
  };

  return (
    <Card sx={{ minWidth: 200, width: 240, height: 240, display: "flex", flexDirection: "column" }}>
      <Box sx={{ textAlign: "center", p: 2 }}>
        <Box position="relative" display="inline-flex">
          <BatteryIndicator variant="determinate" value={battery_level} battery={battery_level} />
          <Box
            top={0}
            left={0}
            bottom={0}
            right={0}
            position="absolute"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="caption" component="div" color="textSecondary">
              {battery_level}%
            </Typography>
          </Box>
        </Box>
        <Typography variant="h6" mt={1}>
          {android_id || "Unknown Device"}
        </Typography>
        <Box mt={1}>
          <Typography variant="caption">
            {model} • {brand}
          </Typography>
        </Box>
        {latitude !== 0 && longitude !== 0 && (
          <Typography variant="caption" display="block">
            📍 {Math.abs(parseFloat(latitude)).toFixed(3)}°{parseFloat(latitude) >= 0 ? "N" : "S"},{" "}
            {Math.abs(parseFloat(longitude)).toFixed(3)}°{parseFloat(longitude) >= 0 ? "E" : "W"}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
          {calculateTimeAgo(payload_timestamp)}
          {pos_pid ? ` · ${pos_pid}` : ""}
        </Typography>
      </Box>
    </Card>
  );
}

DeviceCard.propTypes = {
  battery_level: PropTypes.number.isRequired,
  payload_timestamp: PropTypes.string.isRequired,
  android_id: PropTypes.string,
  pos_pid: PropTypes.string,
  model: PropTypes.string.isRequired,
  brand: PropTypes.string.isRequired,
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function DeviceInfoSP() {
  const { t } = useTranslation("common");
  const { idToken, posPidArray } = useUserContext();
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const androidIDList = useMemo(() => {
    if (!Array.isArray(posPidArray)) return [];
    return posPidArray
      .map((item) => (item?.android_id ? String(item.android_id).trim() : ""))
      .filter(Boolean);
  }, [posPidArray]);
  const androidIdToPid = useMemo(() => {
    const map = new Map();
    (posPidArray || []).forEach((item) => {
      const key = item?.android_id ? String(item.android_id).trim() : "";
      if (key) {
        map.set(key, item?.pos_pid || "");
      }
    });
    return map;
  }, [posPidArray]);

  useEffect(() => {
    const fetchDevices = async () => {
      setIsLoading(true);
      if (!backendUrl) {
        setError("後端 API 位址未設定，請聯絡系統管理員。");
        setIsLoading(false);
        return;
      }
      if (!idToken) {
        setError("User is not authenticated.");
        setIsLoading(false);
        return;
      }
      if (!androidIDList.length) {
        setError("無綁定的裝置，無法載入資料。");
        setIsLoading(false);
        return;
      }
      try {
        const response = await fetch(backendUrl, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `
              query GetBasicInfoByAndroidID($androidID: [String!]!) {
                GetBasicInfoByAndroidID(androidID: $androidID) {
                  device_name
                  payload_timestamp
                  username
                  latitude
                  longitude
                  battery_level
                  model
                  brand
                  license
                  android_id
                }
              }
            `,
            variables: { androidID: androidIDList },
          }),
        });

        if (!response.ok) throw new Error(`Server responded with status: ${response.status}`);
        const data = await response.json();
        if (data.errors) throw new Error(data.errors[0].message || "Error fetching device data");
        const rawDevices = data.data.GetBasicInfoByAndroidID || [];
        const enriched = rawDevices.map((device) => {
          const key = device.android_id ? String(device.android_id).trim() : "";
          return {
            ...device,
            pos_pid: androidIdToPid.get(key) || "",
          };
        });
        setDevices(enriched);
        setError(null);
      } catch (fetchError) {
        console.error("Error fetching device data:", fetchError);
        setError(fetchError.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDevices();
  }, [idToken, androidIDList, backendUrl, androidIdToPid]);

  let content = null;
  if (isLoading) {
    content = (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  } else if (error) {
    content = (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  } else if (!devices.length) {
    content = (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography>No devices found</Typography>
      </Box>
    );
  } else {
    content = (
      <Grid container spacing={2}>
        {devices.map((device, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            {console.log("Device entry:", device)}
            <DeviceCard {...device} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.deviceInfos")}
                </MDTypography>
              </MDBox>
              <MDBox p={3}>{content}</MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default DeviceInfoSP;
